import { useTranslation } from 'react-i18next';
import { BarChart3, Sun, LineChart, Cloud } from 'lucide-react';
import AnimatedNumber from '@/components/base/AnimatedNumber';
import type {
  GenerationForecastLatestResponse, PvActualDayResponse,
  ForecastComparisonResponse, GenerationForecastDayResponse,
} from '@/mocks/generationForecast';

/**
 * KPI-плитки /forecast (редизайн).
 *
 * ДВА ПРАВИЛА, ЯКІ ТУТ ГОЛОВНІ (виправлено 22.09 за зауваженнями з експлуатації):
 *
 * 1. ЧАС СТАНЦІЇ, а не глядача. «Поточна година» береться в часовому поясі СЕС
 *    (Europe/Kyiv), а не з браузера. Інакше двоє людей у різних часових поясах,
 *    дивлячись на ту саму сторінку, бачили різні Факт/Base/ECMWF — об'єктом
 *    генерації й прогнозу в кожній годині є САМЕ станція.
 *
 * 2. МИНУЛІ/МАЙБУТНІ ДОБИ — СУМИ. Показник «на поточну годину» має сенс лише
 *    для сьогодні. Для D-1/D-2 (і D+1/D+2) плитки Факт/Base/ECMWF показують
 *    СУМУ ЗА ДОБУ — тоді вони порівнянні з плиткою «Прогнозована генерація»,
 *    яка завжди добова. Раніше туди підставлялося значення минулої доби на
 *    поточну годину сьогодні — логічного зв'язку не мало.
 */
interface Props {
  latest: GenerationForecastLatestResponse | null;
  loading: boolean;
  pvActualData?: PvActualDayResponse | null;
  ecmwfShadowData?: { hourly: Array<{ hour: string; forecastPowerKw: number | null }> } | null;
  comparisonData?: ForecastComparisonResponse | null;
  dayData?: GenerationForecastDayResponse | null;
  /** Обрана доба (YYYY-MM-DD). Якщо не сьогодні за часом станції — показуємо суми. */
  selectedDate?: string;
  /** Заморожений на D-2 прогноз доби (знімок на момент подання графіка). */
  frozenForecast?: { pvKwh: number | null; frozenAt: string | null } | null;
}

/** Часовий пояс станції. Усі СЕС портфелю — в Україні. */
const STATION_TZ = 'Europe/Kyiv';

/** Поточні дата й година У ЧАСІ СТАНЦІЇ (не в часі браузера). */
function stationNow(): { date: string; hour: number; minute: number } {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: STATION_TZ, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  }).formatToParts(new Date());
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    hour: Number(get('hour')),
    minute: Number(get('minute')),
  };
}

const fmtKwh = (v: number | null | undefined, digits = 1): string =>
  v == null ? '—' : Number.isInteger(v) ? `${v}` : v.toFixed(digits);

const num = (v: any): number | null =>
  v != null && Number.isFinite(Number(v)) ? Number(v) : null;

const hourOfPoint = (p: any): string => {
  const raw = String(p.hour ?? p.forecastHour ?? '');
  const m = raw.match(/(\d{2}:\d{2})/);
  return m ? m[1] : raw.slice(0, 5);
};
const baseValOf = (p: any): number | null => num(p.trueBaseForecastKw ?? p.baseForecastKw);

/** Факт на поточну годину станції (кВт). */
function pickCurrentHourActual(data: PvActualDayResponse | null | undefined, curInt: number): number | null {
  if (!data?.actual?.length) return null;
  const curH = String(curInt).padStart(2, '0') + ':00';
  for (const r of data.actual) {
    if ((r as any).hour === curH) {
      const v = num((r as any).actualPacKw);
      if (v != null) return v;
    }
  }
  const hOnly = (h: string) => parseInt((h || '00').slice(0, 2), 10);
  const cands = data.actual
    .filter((r: any) => num(r.actualPacKw) != null)
    .filter((r: any) => { const hr = hOnly(r.hour); return hr === curInt - 1 || hr === curInt; });
  return cands.length ? num((cands[cands.length - 1] as any).actualPacKw) : null;
}

/** ECMWF на поточну годину станції (кВт). */
function pickCurrentEcmwf(
  data: { hourly: Array<{ hour: string; forecastPowerKw: number | null }> } | null | undefined,
  curInt: number,
): number | null {
  if (!data?.hourly?.length) return null;
  const curH = `${String(curInt).padStart(2, '0')}:00`;
  const exact = data.hourly.find((r) => r.hour === curH);
  if (exact && exact.forecastPowerKw != null) return Number(exact.forecastPowerKw);
  const past = data.hourly.filter((r) => r.hour <= curH && r.forecastPowerKw != null);
  return past.length ? Number(past[past.length - 1].forecastPowerKw) : null;
}

/** Base v0.2.2 на поточну годину станції (кВт). */
function pickCurrentBase(data: ForecastComparisonResponse | null | undefined, curInt: number): number | null {
  const pts = (data as any)?.points;
  if (!Array.isArray(pts) || !pts.length) return null;
  const curH = `${String(curInt).padStart(2, '0')}:00`;
  const exact = pts.find((p: any) => hourOfPoint(p) === curH && baseValOf(p) != null);
  if (exact) return baseValOf(exact);
  const past = pts.filter((p: any) => hourOfPoint(p) <= curH && baseValOf(p) != null);
  return past.length ? baseValOf(past[past.length - 1]) : null;
}

/** Суми за добу (кВт·год): погодинні середні кВт за 24 години ≈ кВт·год. */
const sumActualDay = (d: PvActualDayResponse | null | undefined): number | null => {
  const total = num((d as any)?.totalActualKwhApprox);
  if (total != null) return total;
  if (!d?.actual?.length) return null;
  const vals = d.actual.map((r: any) => num(r.actualPacKw)).filter((v): v is number => v != null);
  return vals.length ? vals.reduce((s, v) => s + v, 0) : null;
};
const sumBaseDay = (d: ForecastComparisonResponse | null | undefined): number | null => {
  const pts = (d as any)?.points;
  if (!Array.isArray(pts) || !pts.length) return null;
  const vals = pts.map(baseValOf).filter((v: number | null): v is number => v != null);
  return vals.length ? vals.reduce((s: number, v: number) => s + v, 0) : null;
};
const sumEcmwfDay = (
  d: { hourly: Array<{ hour: string; forecastPowerKw: number | null }> } | null | undefined,
): number | null => {
  if (!d?.hourly?.length) return null;
  const vals = d.hourly.map((r) => num(r.forecastPowerKw)).filter((v): v is number => v != null);
  return vals.length ? vals.reduce((s, v) => s + v, 0) : null;
};

/**
 * Накопичено з початку доби ДО поточної години станції включно (кВт·год).
 * Погодинне значення — середня потужність години в кВт, тож сума по годинах
 * чисельно дорівнює енергії. Беремо години <= поточної: майбутні години доби
 * у «накопиченому» не мають місця.
 */
const hourNum = (h: string): number => parseInt(String(h ?? '99').slice(0, 2), 10);

/**
 * Вага години в накопиченій сумі: закрита година — 1, поточна — частка, що вже
 * минула, майбутня — 0. Без цього незакрита година рахувалась би як повна й
 * сума завищувалась на недожиту частину (до −1 год генерації на піку).
 */
const hourWeight = (h: number, curInt: number, curMin: number): number =>
  h < curInt ? 1 : h === curInt ? Math.max(0, Math.min(1, curMin / 60)) : 0;

const weighted = (pairs: Array<[number, number | null]>, curInt: number, curMin: number): number | null => {
  const vals = pairs
    .map(([h, v]) => (v == null ? null : v * hourWeight(h, curInt, curMin)))
    .filter((v): v is number => v != null);
  return vals.length ? vals.reduce((s, v) => s + v, 0) : null;
};

const sumActualToNow = (d: PvActualDayResponse | null | undefined, curInt: number, curMin: number): number | null => {
  if (!d?.actual?.length) return null;
  return weighted(d.actual.map((r: any) => [hourNum(r.hour), num(r.actualPacKw)]), curInt, curMin);
};

const sumBaseToNow = (d: ForecastComparisonResponse | null | undefined, curInt: number, curMin: number): number | null => {
  const pts = (d as any)?.points;
  if (!Array.isArray(pts) || !pts.length) return null;
  return weighted(pts.map((p: any) => [hourNum(hourOfPoint(p)), baseValOf(p)]), curInt, curMin);
};

const sumEcmwfToNow = (
  d: { hourly: Array<{ hour: string; forecastPowerKw: number | null }> } | null | undefined,
  curInt: number, curMin: number,
): number | null => {
  if (!d?.hourly?.length) return null;
  return weighted(d.hourly.map((r) => [hourNum(r.hour), num(r.forecastPowerKw)]), curInt, curMin);
};

/** Накопичена сума — з розрядністю тисяч, щоб «43 342» читалось з першого разу. */
const fmtCum = (v: number): string => Math.round(v).toLocaleString('uk-UA').replace(/\u00A0/g, ' ');

export default function ForecastKpiRd({
  latest, loading, pvActualData, ecmwfShadowData, comparisonData, dayData, selectedDate, frozenForecast,
}: Props) {
  const { t } = useTranslation();
  const { date: stationDate, hour: stationHour, minute: stationMinute } = stationNow();
  // Без явної дати вважаємо, що показано поточну добу станції.
  const day = selectedDate || stationDate;
  const isToday = day === stationDate;

  // Плитка 1 — прогноз за ОБРАНУ добу (завжди сума).
  const daily = (latest as any)?.daily?.find((d: any) => d?.day === day);
  const cmpSummary = (comparisonData as any)?.summary;
  const dayHourly = (dayData as any)?.hourly as any[] | undefined;
  const baseDayKwh = dayHourly && dayHourly.length
    ? dayHourly.reduce((s, h) => s + (Number(h?.forecastEnergyKwh) || 0), 0) : null;
  const totalKwh = baseDayKwh ?? daily?.forecastEnergyKwh ?? cmpSummary?.baseForecastKwhApprox ?? null;

  // Плитки 2-4 — «зараз» лише для поточної доби станції, інакше сума за добу.
  const perDay = t('ak_fc_per_day');
  // Для поточної доби суфікси в підписах не потрібні: поруч зі значенням тепер
  // стоїть накопичена сума, і вона сама пояснює, що головне число — «зараз».
  const unit = isToday ? t('ak_fc_kw') : t('ak_fc_kwh_2');
  const actualVal = isToday ? pickCurrentHourActual(pvActualData, stationHour) : sumActualDay(pvActualData);
  const baseVal = isToday ? pickCurrentBase(comparisonData, stationHour) : sumBaseDay(comparisonData);
  const ecmwfVal = isToday ? pickCurrentEcmwf(ecmwfShadowData, stationHour) : sumEcmwfDay(ecmwfShadowData);

  // Накопичено з початку доби станом на поточну годину — другим, дрібнішим і
  // тьмянішим числом поруч із миттєвим. Лише для сьогодні: на минулих добах
  // головне число вже і є добова сума, дубль там не потрібен.
  const cumActual = isToday ? sumActualToNow(pvActualData, stationHour, stationMinute) : null;
  const cumBase = isToday ? sumBaseToNow(comparisonData, stationHour, stationMinute) : null;
  const cumEcmwf = isToday ? sumEcmwfToNow(ecmwfShadowData, stationHour, stationMinute) : null;

  // Два стани прогнозу на першій плитці:
  //   ГОЛОВНЕ ЧИСЛО — заморожений на D-2 знімок: прогноз, який був відомий при
  //     настанні доби Д і під який подавався графік до MMS;
  //   ПІДРЯДОК — поточний (живий) прогон моделі + дрейф Δ.
  // Порядок саме такий: живий прогноз уже є на плитці «Base», тож головним його
  // ставити не можна — плитки дублювали б одна одну.
  const frozenKwh = frozenForecast?.pvKwh ?? null;
  const liveKwh = totalKwh;
  const hasFrozen = frozenKwh != null;
  // Розбіжність добової суми з прогнозом D-2 — показуємо просто після значення
  // на добових плитках (Факт / Base / ECMWF). Для поточної доби сенсу немає:
  // там миттєві кВт, а не накопичення, тож порівнювати з добовою сумою не можна.
  const deltaVsFrozen = (v: number | null): number | null =>
    (!isToday && hasFrozen && frozenKwh && v != null) ? ((v - frozenKwh) / Math.abs(frozenKwh)) * 100 : null;

  // У плитках тримаємо СИРЕ число, а не готовий рядок: AnimatedNumber
  // перекручує значення до нового й форматує кожен проміжний кадр сам.
  const items = [
    { id: 'forecast', label: `${t('fc_kpi_total_energy')}${hasFrozen ? ' (D-2)' : ''}`,
      raw: hasFrozen ? frozenKwh : liveKwh, unit: t('ak_fc_kwh_2'), Icon: BarChart3, accent: true, delta: null, cum: null },
    { id: 'actual', label: isToday ? t('fc_kpi_actual_pv') : `${t('fc_kpi_actual_pv')} (${perDay})`,
      raw: actualVal, unit, Icon: Sun, accent: false, delta: deltaVsFrozen(actualVal), cum: cumActual },
    { id: 'base', label: isToday ? t('ak_fc_base_forecast') : `Base (${perDay})`, raw: baseVal, unit, Icon: LineChart, accent: false,
      delta: deltaVsFrozen(baseVal), cum: cumBase },
    { id: 'ecmwf', label: isToday ? t('ak_fc_ecmwf_forecast') : `ECMWF (${perDay})`, raw: ecmwfVal, unit, Icon: Cloud, accent: false,
      delta: deltaVsFrozen(ecmwfVal), cum: cumEcmwf },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {items.map((k) => (
        <div key={k.id} className="tile p-4 flex items-center gap-3.5">
          <div
            className="w-10 h-10 rounded-tile-sm flex items-center justify-center shrink-0"
            style={{ background: 'var(--surface-2)' }}
          >
            <k.Icon size={18} strokeWidth={1.75} style={{ color: 'var(--text-muted)' }} />
          </div>
          {/* flex-1 обов'язковий: без нього блок стискається до ширини тексту,
              всередині немає вільного місця, і `ml-auto` на сумі нема куди
              штовхати — суми «пливли» за шириною головного числа. */}
          <div className="min-w-0 flex-1">
            <div className="text-[11px] text-muted mb-0.5 truncate">{k.label}</div>
            <div className="flex items-baseline gap-1 flex-wrap">
              {/* AnimatedNumber НЕ ховаємо за loading: при зміні доби
                  fetchLatest() піднімає прапорець, і тернар розмонтовував би
                  компонент — після монтування він стартував би вже з новим
                  числом, тож анімації не було б видно взагалі. Сирі значення
                  під час перезавантаження не скидаються, тож плитка тримає
                  старе число й плавно переходить до нового. */}
              <span className="font-display text-2xl font-normal text-ink leading-none tabular-nums">
                <AnimatedNumber id={k.id} value={k.raw} format={(v) => fmtKwh(v, 1)} />
              </span>
              <span className="text-[11px] text-dim">{k.unit}</span>
              {/* Накопичено з початку доби: дрібніше й тьмяніше за головне
                  число — це контекст, а не самостійний показник.
                  `ml-auto` притискає блок до правого краю плитки: колонки сітки
                  рівні, тож суми трьох плиток стають на одну вертикаль і
                  читаються як стовпчик, а не «пливуть» за шириною головного
                  числа. Σ — загальноприйняте позначення суми. */}
              {(k as any).cum != null && (
                <span className="text-[13px] tabular-nums whitespace-nowrap ml-auto pl-2"
                  style={{ color: 'var(--text-dim)' }}
                  title={t('ak_fc_accumulated_since_the_start_of_the_day', { hh: String(stationHour).padStart(2, '0'), mm: String(stationMinute).padStart(2, '0') })}>
                  Σ {fmtCum((k as any).cum)}{' '}
                  {/* Одиниця — тим самим кеглем, що й «кВт» головного числа
                      (11px): одиниці виміру на сторінці мають бути однакові,
                      різниться лише саме число. */}
                  <span className="text-[11px]">{t('ak_fc_kwh_2')}</span>
                </span>
              )}
              {!loading && (k as any).delta != null && (
                <span className="text-[11px] font-semibold whitespace-nowrap ml-auto pl-2"
                  title={frozenForecast?.frozenAt
                    ? t('ak_fc_deviation_from_the_d_2_forecast_snapsh', { at: frozenForecast.frozenAt }) : undefined}
                  style={{ color: (k as any).delta > 0 ? 'var(--ok)' : (k as any).delta < 0 ? 'var(--warn)' : 'var(--text-dim)' }}>
                  Δ {(k as any).delta > 0 ? '+' : (k as any).delta < 0 ? '−' : ''}{Math.abs((k as any).delta).toFixed(1)}%
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
